import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { FormMessageHOC } from 'common';
import { DefaultButton, PageLayout } from 'common/components';
import { EditIcon, BasketIcon } from 'common/icons';
import { BaseButton, BasicCRMModal, SearchBar } from 'crm/_components';
import { UpdatedCRMTable } from 'crm/_newComponents';
import { getTags } from 'crm/link_distribution/_redux/actions';
import LinkDistributionModal from 'crm/link_distribution/_components/LinkDistributionModal/LinkDistributionModal';
import 'crm/link_distribution/_pages/LinkDistributionPage/LinkDistributionPage.scss';

import {
  createManagedLinkDistribution,
  deleteManagedLinkDistribution,
  getManagedLinkDistributions,
  toggleManagedLinkDistributionSalesVisibility,
  updateManagedLinkDistribution,
} from '../../api';

const LinkDistributionPage = ({ location, history, perms, tags, getTags: loadTags, showFormSuccessMessage }) => {
  const [list, setList] = useState([]);
  const [count, setCount] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('add');
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [visibilityItem, setVisibilityItem] = useState(null);

  const query = useMemo(() => ({ page: 1, page_size: 10, ...queryString.parse(location.search) }), [location.search]);
  const [searchInput, setSearchInput] = useState(query.search || '');
  const [tagInput, setTagInput] = useState(query.tag || '');
  const [pageInput, setPageInput] = useState(String(query.page));
  const canAdd = !!perms?.addCrmLinkDistribution;
  const canEdit = !!perms?.editCrmLinkDistribution;
  const canDelete = !!perms?.deleteCrmLinkDistribution;
  const canToggleSalesVisibility = !!perms?.showHideCrmLinkDistributionForSales;
  const hasActions = canEdit || canDelete || canToggleSalesVisibility;

  const loadList = useCallback(async () => {
    setIsLoaded(false);
    try {
      const { data } = await getManagedLinkDistributions(query);
      setList(Array.isArray(data) ? data : data?.results || []);
      setCount(Array.isArray(data) ? data.length : data?.count || 0);
    } finally {
      setIsLoaded(true);
    }
  }, [query]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  useLayoutEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    setSearchInput(query.search || '');
    setTagInput(query.tag || '');
  }, [query.search, query.tag]);

  useEffect(() => {
    setPageInput(String(query.page));
  }, [query.page]);

  const updateSearch = newFilters => {
    history.replace({
      ...location,
      search: queryString.stringify({ ...query, page: 1, ...newFilters }),
    });
  };

  const executeSearch = () => updateSearch({ search: searchInput, tag: tagInput });
  const clearFilters = () => {
    setSearchInput('');
    setTagInput('');
    updateSearch({ search: '', tag: '' });
  };

  const pagesCount = Math.max(1, Math.ceil(count / Number(query.page_size)));
  const changePage = page => {
    const nextPage = Number(page);
    if (!Number.isInteger(nextPage) || nextPage < 1 || nextPage > pagesCount) return;
    history.replace({ ...location, search: queryString.stringify({ ...query, page: nextPage }) });
  };
  const submitPage = () => {
    changePage(pageInput);
    setPageInput(String(query.page));
  };

  const handleAdd = () => {
    setModalType('add');
    setEditingItem({ fxSetting: '', metalsSetting: '', commoditiesSetting: '' });
    setIsModalOpen(true);
  };

  const handleEdit = item => {
    setModalType('edit');
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const onSubmit = async values => {
    const payload = {
      ...values,
      ibType: values.ibType || '',
      fxSetting: values.fxSetting || '0.00',
      metalsSetting: values.metalsSetting || '0.00',
      commoditiesSetting: values.commoditiesSetting || '0.00',
    };
    await (modalType === 'add'
      ? createManagedLinkDistribution(payload)
      : updateManagedLinkDistribution({ ...payload, id: editingItem.id }));
    setIsModalOpen(false);
    showFormSuccessMessage(modalType === 'add' ? 'Added successfully' : 'Updated successfully');
    loadList();
  };

  const onConfirmDelete = async () => {
    await deleteManagedLinkDistribution(deletingItem.id);
    setDeletingItem(null);
    showFormSuccessMessage('Deleted successfully');
    loadList();
  };

  const onConfirmVisibility = async () => {
    await toggleManagedLinkDistributionSalesVisibility(visibilityItem.id);
    setVisibilityItem(null);
    showFormSuccessMessage('Updated successfully');
    loadList();
  };

  const columns = useMemo(
    () => [
      {
        Header: () => 'No.',
        accessor: 'id',
        Cell: ({ rowData }) =>
          Number(query.page - 1) * Number(query.page_size) + list.findIndex(item => item.id === rowData.id) + 1,
      },
      { Header: () => 'Type', accessor: 'ibType', Cell: ({ rowData }) => rowData.ibType || '---' },
      {
        Header: () => 'Attribute',
        accessor: 'attribute',
        Cell: ({ rowData: { attribute } }) => (
          <span className={`attr-badge ${attribute}`}>{attribute === 'mark-up' ? 'Mark-up' : 'Basic'}</span>
        ),
      },
      { Header: () => 'FX', accessor: 'fxSetting', Cell: ({ rowData }) => rowData.fxSetting || '---' },
      { Header: () => 'METALS', accessor: 'metalsSetting', Cell: ({ rowData }) => rowData.metalsSetting || '---' },
      {
        Header: () => 'COMMODITIES',
        accessor: 'commoditiesSetting',
        Cell: ({ rowData }) => rowData.commoditiesSetting || '---',
      },
      {
        Header: () => 'Tag',
        accessor: 'tag',
        Cell: ({ rowData: { tag } }) => {
          const tagObj = tags.find(item => item.id === tag || item.id === parseInt(tag, 10));
          return <span className="tag-badge">{tagObj ? tagObj.name : '---'}</span>;
        },
      },
      ...(hasActions
        ? [
            {
              Header: () => 'Actions',
              accessor: 'actions',
              Cell: ({ rowData }) => (
                <div className="action-buttons">
                  {canEdit && (
                    <button type="button" className="edit-btn" onClick={() => handleEdit(rowData)}>
                      <EditIcon width={14} height={14} /> Edit
                    </button>
                  )}
                  {canDelete && (
                    <button type="button" className="delete-btn" onClick={() => setDeletingItem(rowData)}>
                      <BasketIcon width={14} height={14} /> Delete
                    </button>
                  )}
                  {canToggleSalesVisibility && (
                    <button type="button" className="sales-visibility-btn" onClick={() => setVisibilityItem(rowData)}>
                      <SalesVisibilityIcon isVisible={rowData.isShowForSales} />
                      {rowData.isShowForSales ? 'Hide from sales' : 'Show for sales'}
                    </button>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [canDelete, canEdit, canToggleSalesVisibility, hasActions, list, query.page, query.page_size, tags]
  );

  const tableSetup = useMemo(
    () => ({
      columns,
      initSubRow: [],
      initGrid: hasActions
        ? '60px 1.5fr 1fr 1fr 1fr 1fr 1.5fr minmax(250px, 1.8fr)'
        : '60px 1.5fr 1fr 1fr 1fr 1fr 1.5fr',
    }),
    [columns, hasActions]
  );

  const deletionLabel = `${deletingItem?.ibType || '---'} (${
    tags.find(tag => tag.id === deletingItem?.tag)?.name || '---'
  })`;
  const isShowingForSales = !!visibilityItem?.isShowForSales;

  return (
    <PageLayout captionSlug="crmLinkDistribution">
      <div className="IBTab BackofficeLinkDistributionPage">
        <div className="IBTab__filters">
          <div className="filters-left">
            <div className="filter-item">
              <span className="filter-label">Type</span>
              <SearchBar
                initValue={searchInput}
                onInputChange={setSearchInput}
                onEnter={executeSearch}
                placeholder="Type"
              />
            </div>
            <div className="filter-item">
              <span className="filter-label">Tag</span>
              <select value={tagInput} onChange={event => setTagInput(event.target.value)} className="tag-select">
                <option value="">Tag</option>
                {tags.map(tag => (
                  <option key={tag.id} value={tag.id}>
                    {tag.name}
                  </option>
                ))}
              </select>
            </div>
            <BaseButton textId="justSearch" onClick={executeSearch} />
            <BaseButton textId="justReset" color="white" onClick={clearFilters} />
          </div>
          <div className="filters-right">
            {canAdd && <DefaultButton textId="justAdd" plus filled onClick={handleAdd} />}
          </div>
        </div>

        <div className="IBTab__table">
          {isLoaded && (
            <>
              <UpdatedCRMTable
                data={list}
                tableSetup={tableSetup}
                placeholderTextId="crmThereAreNoItemsMatchingParameters"
              />
              {!!count && (
                <div className="LinkDistributionPagination">
                  <div className="LinkDistributionPagination__page-size">
                    <select
                      value={query.page_size}
                      onChange={event => updateSearch({ page_size: event.target.value })}
                      aria-label="Items per page">
                      {[10, 50, 100].map(size => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="LinkDistributionPagination__navigation">
                    <span>Page</span>
                    <input
                      type="number"
                      min="1"
                      max={pagesCount}
                      value={pageInput}
                      onChange={event => setPageInput(event.target.value)}
                      onBlur={submitPage}
                      onKeyDown={event => {
                        if (event.key === 'Enter') event.currentTarget.blur();
                      }}
                    />
                    <span>of {pagesCount}</span>
                    <DefaultButton
                      textId="justPrevious"
                      previous
                      onClick={() => changePage(Number(query.page) - 1)}
                      disabled={Number(query.page) <= 1}
                    />
                    <DefaultButton
                      textId="justNext"
                      next
                      onClick={() => changePage(Number(query.page) + 1)}
                      disabled={Number(query.page) >= pagesCount}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <BasicCRMModal
          isOpen={isModalOpen}
          captionId={modalType === 'add' ? 'justAdd' : 'justEdit'}
          className="LinkDistributionModal__modal"
          onRequestClose={() => setIsModalOpen(false)}>
          <LinkDistributionModal
            initialValues={editingItem}
            onSubmit={onSubmit}
            onClose={() => setIsModalOpen(false)}
          />
        </BasicCRMModal>

        <BasicCRMModal isOpen={!!deletingItem} captionId="confirmText" onRequestClose={() => setDeletingItem(null)}>
          <div style={{ padding: '20px 20px 0', fontSize: '16px', color: '#5f636d', textAlign: 'center' }}>
            Delete &quot;{deletionLabel}&quot;? This action cannot be undone.
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', padding: '20px' }}>
            <BaseButton color="white" textId="justCancel" onClick={() => setDeletingItem(null)} />
            <BaseButton color="red" textId="justDelete" onClick={onConfirmDelete} />
          </div>
        </BasicCRMModal>

        <BasicCRMModal isOpen={!!visibilityItem} captionId="confirmText" onRequestClose={() => setVisibilityItem(null)}>
          <div style={{ padding: '20px 20px 0', fontSize: '16px', color: '#5f636d', textAlign: 'center' }}>
            {isShowingForSales ? 'Hide this link distribution from sales?' : 'Show this link distribution for sales?'}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', padding: '20px' }}>
            <BaseButton color="white" textId="justCancel" onClick={() => setVisibilityItem(null)} />
            <BaseButton textId="justConfirm" onClick={onConfirmVisibility} />
          </div>
        </BasicCRMModal>
      </div>
    </PageLayout>
  );
};

const SalesVisibilityIcon = ({ isVisible }) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
    <path
      d="M2.75 12s3.35-5.75 9.25-5.75S21.25 12 21.25 12 17.9 17.75 12 17.75 2.75 12 2.75 12Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="2.35" stroke="currentColor" strokeWidth="1.8" />
    {isVisible && <path d="M4 4 20 20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />}
  </svg>
);

LinkDistributionPage.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  perms: PropTypes.object.isRequired,
  tags: PropTypes.array.isRequired,
  getTags: PropTypes.func.isRequired,
  showFormSuccessMessage: PropTypes.func.isRequired,
};

SalesVisibilityIcon.propTypes = { isVisible: PropTypes.bool.isRequired };

const mapStateToProps = state => ({
  perms: state.user.profile?.perms || {},
  tags: state.crm.linkDistribution.tags || [],
});

const mapDispatchToProps = { getTags: () => getTags.request() };

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps), FormMessageHOC)(LinkDistributionPage);
